import { useState } from 'react';

interface DestinationInquiryFormProps {
  destinationName: string;
}

export default function DestinationInquiryForm({ destinationName }: DestinationInquiryFormProps) {
  const [formData, setFormData] = useState({
    destination: destinationName,
    name: '',
    email: '',
    charterStartDate: '',
    charterEndDate: '',
    numberOfGuests: '',
    vesselLength: '',
    budget: '',
    interiorStyle: '',
    mustHaves: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const form = e.currentTarget;
      const formDataToSend = new URLSearchParams();
      
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d631rvpov5qgbon31pg0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          destination: destinationName,
          name: '',
          email: '',
          charterStartDate: '',
          charterEndDate: '',
          numberOfGuests: '',
          vesselLength: '',
          budget: '',
          interiorStyle: '',
          mustHaves: ''
        });
        form.reset();
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-slate-50 to-blue-50/30 py-20">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-slate-900 mb-4">
            Inquire About {destinationName}
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Ready to explore {destinationName}? Share your charter preferences and our team will create a personalized itinerary for your dream voyage.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
          <form 
            id="destination-inquiry-form"
            data-readdy-form 
            onSubmit={handleSubmit}
            className="space-y-6"
          >
            {/* Hidden destination field */}
            <input 
              type="hidden" 
              name="destination" 
              value={destinationName}
            />

            {/* Contact Information */}
            <div className="border-b border-slate-200 pb-6">
              <h3 className="text-xl font-semibold text-slate-900 mb-4">Contact Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
                    placeholder="john@example.com"
                  />
                </div>
              </div>
            </div>

            {/* Charter Details */}
            <div className="border-b border-slate-200 pb-6">
              <h3 className="text-xl font-semibold text-slate-900 mb-4">Charter Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="charterStartDate" className="block text-sm font-medium text-slate-700 mb-2">
                    Charter Start Date *
                  </label>
                  <input
                    type="date"
                    id="charterStartDate"
                    name="charterStartDate"
                    required
                    value={formData.charterStartDate}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="charterEndDate" className="block text-sm font-medium text-slate-700 mb-2">
                    Charter End Date *
                  </label>
                  <input
                    type="date"
                    id="charterEndDate"
                    name="charterEndDate"
                    required
                    value={formData.charterEndDate}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="numberOfGuests" className="block text-sm font-medium text-slate-700 mb-2">
                    Number of Guests *
                  </label>
                  <select
                    id="numberOfGuests"
                    name="numberOfGuests"
                    required
                    value={formData.numberOfGuests}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
                  >
                    <option value="">Select number of guests</option>
                    <option value="2-4">2-4 guests</option>
                    <option value="5-8">5-8 guests</option>
                    <option value="9-12">9-12 guests</option>
                    <option value="13-16">13-16 guests</option>
                    <option value="17+">17+ guests</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="vesselLength" className="block text-sm font-medium text-slate-700 mb-2">
                    Preferred Vessel Length *
                  </label>
                  <select
                    id="vesselLength"
                    name="vesselLength"
                    required
                    value={formData.vesselLength}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
                  >
                    <option value="">Select vessel length</option>
                    <option value="30-50m">30-50m (98-164ft)</option>
                    <option value="50-70m">50-70m (164-230ft)</option>
                    <option value="70-90m">70-90m (230-295ft)</option>
                    <option value="90-110m">90-110m (295-361ft)</option>
                    <option value="110m+">110m+ (361ft+)</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Budget & Preferences */}
            <div className="border-b border-slate-200 pb-6">
              <h3 className="text-xl font-semibold text-slate-900 mb-4">Budget & Preferences</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="budget" className="block text-sm font-medium text-slate-700 mb-2">
                    Weekly Budget (USD) *
                  </label>
                  <select
                    id="budget"
                    name="budget"
                    required
                    value={formData.budget}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
                  >
                    <option value="">Select budget range</option>
                    <option value="100k-250k">$100,000 - $250,000</option>
                    <option value="250k-500k">$250,000 - $500,000</option>
                    <option value="500k-1m">$500,000 - $1,000,000</option>
                    <option value="1m-2m">$1,000,000 - $2,000,000</option>
                    <option value="2m+">$2,000,000+</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="interiorStyle" className="block text-sm font-medium text-slate-700 mb-2">
                    Preferred Interior Style *
                  </label>
                  <select
                    id="interiorStyle"
                    name="interiorStyle"
                    required
                    value={formData.interiorStyle}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
                  >
                    <option value="">Select interior style</option>
                    <option value="Modern & Minimalist">Modern & Minimalist</option>
                    <option value="Classic & Traditional">Classic & Traditional</option>
                    <option value="Contemporary Luxury">Contemporary Luxury</option>
                    <option value="Beach House Casual">Beach House Casual</option>
                    <option value="Art Deco Glamour">Art Deco Glamour</option>
                    <option value="No Preference">No Preference</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Must Haves */}
            <div>
              <label htmlFor="mustHaves" className="block text-sm font-medium text-slate-700 mb-2">
                Must-Haves & Special Requirements
              </label>
              <textarea
                id="mustHaves"
                name="mustHaves"
                rows={5}
                maxLength={500}
                value={formData.mustHaves}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm resize-none"
                placeholder="Tell us about any special requirements, amenities, or experiences you're looking for (e.g., beach club, helipad, water toys, spa facilities, dietary requirements, special occasions, etc.)"
              />
              <p className="text-xs text-slate-500 mt-1">{formData.mustHaves.length}/500 characters</p>
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold py-4 px-8 rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap text-base shadow-lg hover:shadow-xl"
              >
                {isSubmitting ? 'Sending Inquiry...' : 'Submit Charter Inquiry'}
              </button>
            </div>

            {/* Status Messages */}
            {submitStatus === 'success' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <i className="ri-check-line text-2xl text-green-600 mb-2"></i>
                <p className="text-green-800 font-medium">Thank you for your inquiry!</p>
                <p className="text-green-700 text-sm mt-1">Our team will contact you within 24 hours to discuss your {destinationName} charter.</p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                <i className="ri-error-warning-line text-2xl text-red-600 mb-2"></i>
                <p className="text-red-800 font-medium">Something went wrong</p>
                <p className="text-red-700 text-sm mt-1">Please try again or contact us directly at charters@sygyachting.com</p>
              </div>
            )}
          </form>
        </div>

        {/* Contact Alternative */}
        <div className="text-center mt-8">
          <p className="text-slate-600 text-sm">
            Prefer to speak with us directly? Email us at{' '}
            <a href="mailto:charters@sygyachting.com" className="text-teal-600 hover:text-teal-700 font-medium whitespace-nowrap">
              charters@sygyachting.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
